import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// Single source of truth for auth across the whole app.
//
// Previously `useAuth` was a bare per-component hook: each of the ~31 call
// sites mounted its OWN onAuthStateChange listener + getSession, and every
// auth event (token refresh, tab-focus SIGNED_IN re-emit, INITIAL_SESSION)
// handed back a brand-new `user` object reference. Every `[user]`-keyed data
// effect across the app then re-fired at once — the refetch flood. This
// provider collapses that to ONE listener and, critically, keeps the `user`
// reference STABLE across benign events (it only changes when the user id
// actually changes), so downstream effects stop re-running.

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** Resolved once per user from profiles — shared by every consumer. */
  businessId: string | null;
  signIn: (email: string, password: string) => ReturnType<typeof supabase.auth.signInWithPassword>;
  signOut: () => ReturnType<typeof supabase.auth.signOut>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState<string | null>(null);

  useEffect(() => {
    // Keep the user reference stable across token refreshes / focus re-emits:
    // swap it only when the id changes. The session token still updates so
    // callers that need a fresh access_token get it; only `user` identity is
    // pinned, which is what downstream effects depend on.
    const apply = (next: Session | null) => {
      setSession(next);
      setUser((prev) => (prev?.id === next?.user?.id ? prev : next?.user ?? null));
      setLoading(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => apply(next));

    supabase.auth.getSession().then(({ data: { session } }) => apply(session));

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve business_id ONCE per user (primitive dep — not the object), so the
  // always-mounted consumers (Sidebar, DashboardLayout, location pages) share a
  // single lookup instead of each firing their own profiles?select=business_id.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) {
      setBusinessId(null);
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("business_id")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setBusinessId(data?.business_id ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const signIn = useCallback(
    (email: string, password: string) => supabase.auth.signInWithPassword({ email, password }),
    [],
  );
  // scope:'local' — sign out THIS device only. The default (global) revokes
  // every session the user has, so logging out in one browser killed all
  // other devices' sessions (and any server-side auth.getUser on their JWTs
  // started failing with "Session from session_id claim does not exist").
  const signOut = useCallback(() => supabase.auth.signOut({ scope: "local" }), []);

  const value = useMemo(
    () => ({ user, session, loading, businessId, signIn, signOut }),
    [user, session, loading, businessId, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/**
 * Shared business id, resolved once in the provider. The optional argument is
 * accepted only for backward compatibility with the previous `useBusinessId(user)`
 * call sites and is ignored.
 */
export function useBusinessId(_user?: User | null): string | null {
  return useAuth().businessId;
}
